import { FC, useMemo } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MenuButton } from 'components/buttons'
import { Spinner } from 'components/loaders'
import { useAdminProjects } from '@features/admin/hooks/useAdminProjects'
import { useProjectIntegrations } from 'hooks/useProjectIntegrations'
import { useRenameProject } from '../../hooks/useRenameProject'
import { useToggleProjectArchival } from '../../hooks/useToggleProjectArchival'
import { useLinkIntegrationProject } from '../../hooks/useLinkIntegrationProject'
import { useUnlinkIntegrationProject } from '../../hooks/useUnlinkIntegrationProject'
import { useLinkWorkdayProject } from '../../hooks/useLinkWorkdayProject'
import { useUnlinkWorkdayProject } from '../../hooks/useUnlinkWorkdayProject'
import { useToggleAutoApproval } from '../../hooks/useToggleAutoApproval'
import { faEdit, faArchive, faBoxOpen } from '@fortawesome/pro-solid-svg-icons'
import { ApproversSection } from './components/ApproversSection'
import { WorkdaySection } from './components/WorkdaySection'
import { PMISection } from './components/PMISection'
import { TasksSection } from './components/TasksSection'
import { NewHeader as Header } from 'components/page-header'
import {
  EditApproverModal,
  AddApproverModal,
  useApproverManagement,
  AutoApprovalSection,
} from '@features/admin/features/approval-domain-approvers'

export const ProjectDetails: FC = () => {
  const { t } = useTranslation()
  const { projectId } = useParams<{ projectId: string }>()
  const {
    data: projects,
    isLoading: projectsLoading,
    refetch,
  } = useAdminProjects()
  const { data: projectIntegrations, isLoading: integrationsLoading } =
    useProjectIntegrations()

  const project = useMemo(
    () => projects?.find((p) => p.id === Number(projectId)),
    [projects, projectId],
  )

  const { renameProject } = useRenameProject()
  const { toggleProjectArchival } = useToggleProjectArchival()
  const { linkIntegrationProject } = useLinkIntegrationProject()
  const { unlinkIntegrationProject } = useUnlinkIntegrationProject()
  const { linkWorkdayProject } = useLinkWorkdayProject()
  const { unlinkWorkdayProject } = useUnlinkWorkdayProject()
  const toggleAutoApprovalMutation = useToggleAutoApproval()

  const {
    editingApprover,
    setEditingApprover,
    showAddApproverModal,
    setShowAddApproverModal,
    handleDeleteApprover,
    handleApproverSuccess,
    handleApproverError,
  } = useApproverManagement(project, () => void refetch())

  if (projectsLoading || integrationsLoading) {
    return <Spinner />
  }

  if (!project || !projectIntegrations) {
    return <Navigate to="/projects" replace />
  }

  const actionMenuItems = [
    {
      icon: faEdit,
      label: t('features.projectsAdmin.renameProject'),
      onClick: () => renameProject(project),
    },
    {
      icon: project.archivedAt ? faBoxOpen : faArchive,
      label: project.archivedAt
        ? t('features.projectsAdmin.unarchiveProject')
        : t('features.projectsAdmin.archiveProject'),
      onClick: () => toggleProjectArchival(project),
    },
  ]

  const integrationLinks = projectIntegrations.map((integration) => {
    const link = project.integrationProjectLinks.find(
      (link) => link.projectIntegration.id === integration.id,
    )
    return { integration, link }
  })

  return (
    <>
      <div className="flex flex-col flex-grow">
        <Header
          title={project.name}
          renderRightSlot={() => (
            <MenuButton title={t('common.action')} menuItems={actionMenuItems}>
              {t('common.action')}
            </MenuButton>
          )}
        />
        <div className="flex flex-col p-8">
          <WorkdaySection
            workdayProjectId={project.workdayProject?.id}
            workdayProjectName={project.workdayProject?.name}
            onLink={() => linkWorkdayProject(project)}
            onUnlink={() => unlinkWorkdayProject(project)}
          />
          <PMISection
            project={project}
            integrationLinks={integrationLinks}
            onLink={linkIntegrationProject}
            onUnlink={unlinkIntegrationProject}
          />
          <TasksSection project={project} />
          <ApproversSection
            project={project}
            onAdd={() => setShowAddApproverModal(true)}
            onEdit={(approver) => setEditingApprover(approver.id)}
            onDelete={handleDeleteApprover}
          />
          <AutoApprovalSection
            approvalDomainType="project"
            approvalDomain={project}
            mutateFn={toggleAutoApprovalMutation.mutate}
          />
        </div>
      </div>
      {editingApprover && (
        <EditApproverModal
          approvalDomainId={project.globalId}
          approverId={editingApprover}
          onClose={() => setEditingApprover(undefined)}
          onError={handleApproverError}
          onSuccess={handleApproverSuccess}
        />
      )}
      {showAddApproverModal && (
        <AddApproverModal
          approvalDomainId={project.globalId}
          onClose={() => setShowAddApproverModal(false)}
          onError={handleApproverError}
          onSuccess={handleApproverSuccess}
          userIdsAlreadyAsApprover={
            new Set(project.approverUsers.map((u) => u.id))
          }
        />
      )}
    </>
  )
}
